import React, { useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Avatar,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
} from '@mui/material';
import { Bell } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAvatarSrc } from '../../../utils/avatarConstants';
import { navigateFromNotification } from '../../../utils/notificationNavigation';

function MobileTopBar({
  userProfile,
  onProfileMenuOpen,
  notifications = [],
  unreadCount = 0,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteAllNotifications,
  _onDeleteNotification,
  onNotificationPageClick,
}) {
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNotificationClick = (event) => {
    if (onNotificationPageClick) {
      onNotificationPageClick(event);
    } else {
      setNotificationAnchor(event.currentTarget);
    }
  };

  const handleNotificationClose = useCallback(() => {
    setNotificationAnchor(null);
  }, []);

  const handleNotificationItemClick = useCallback(
    async (notification) => {
      if (!notification.isRead && onMarkAsRead) {
        await onMarkAsRead(notification._id);
      }
      navigateFromNotification(navigate, notification, {
        isSupplier: userProfile?.role === 'supplier',
      });
      handleNotificationClose();
    },
    [onMarkAsRead, navigate, handleNotificationClose, userProfile?.role]
  );

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const handleMarkAllRead = useCallback(async () => {
    if (onMarkAllAsRead) {
      try {
        await onMarkAllAsRead();
      } catch {
        // Error handled silently - user can retry
      }
    }
    handleNotificationClose();
  }, [onMarkAllAsRead, handleNotificationClose]);

  const handleDeleteAll = useCallback(async () => {
    if (onDeleteAllNotifications) {
      try {
        await onDeleteAllNotifications();
      } catch {
        // Error handled silently - user can retry
      }
    }
    handleNotificationClose();
  }, [onDeleteAllNotifications, handleNotificationClose]);

  const isSupplier = typeof window !== 'undefined' && location.pathname.startsWith('/supplier/');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: 56,
      }}
    >
      <IconButton
        color="inherit"
        onClick={handleNotificationClick}
        aria-label="Notifications"
        sx={{ color: 'text.primary' }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <Bell size={24} weight="regular" />
        </Badge>
      </IconButton>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={() => navigate(isSupplier ? '/supplier/dashboard' : '/dashboard')}
      >
        <img
          src={`${process.env.PUBLIC_URL}/img/logo.svg`}
          alt="Logo"
          style={{ height: 30 }}
        />
      </Box>

      <IconButton
          color="inherit"
          onClick={onProfileMenuOpen}
          aria-label="Profile"
          sx={{ color: 'text.primary', p: 0 }}
        >
          <Avatar
          src={getAvatarSrc(userProfile?.profilePicture || userProfile?.avatar)}
          alt={userProfile?.name || userProfile?.firstName || 'User'}
          sx={{ width: 36, height: 36 }}
        >
          {!userProfile?.profilePicture && !userProfile?.avatar &&
            (userProfile?.firstName?.[0] || userProfile?.name?.[0] || 'U')}
        </Avatar>
      </IconButton>

      <Popover
        open={Boolean(notificationAnchor)}
        anchorEl={notificationAnchor}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { minWidth: 320, maxWidth: 360, maxHeight: 400 },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllRead}>
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button size="small" color="error" onClick={handleDeleteAll}>
                Clear
              </Button>
            )}
          </Box>
        </Box>
        <List sx={{ maxHeight: 320, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText primary="No notifications" secondary="You're all caught up!" />
            </ListItem>
          ) : (
            notifications.slice(0, 20).map((n) => (
              <ListItem
                key={n._id}
                button
                onClick={() => handleNotificationItemClick(n)}
                sx={{
                  bgcolor: n.isRead ? 'transparent' : 'action.hover',
                }}
              >
                <ListItemText
                  primary={n.title}
                  secondary={formatTimeAgo(n.createdAt)}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: n.isRead ? 400 : 600 }}
                />
              </ListItem>
            ))
          )}
        </List>
      </Popover>
    </Box>
  );
}

export default MobileTopBar;
